import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react'
import { Box, Typography, IconButton, Tooltip, Chip, Collapse } from '@mui/material'
import { FancyButton } from './FancyButtons'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import UndoIcon from '@mui/icons-material/Undo'
import TravelExploreIcon from '@mui/icons-material/TravelExplore'
import { FS_MICRO, FS_XS, FS_XL, FS_SM } from '../common/theme'
import { SourceDocCard } from './SourceDocCard'
import TaPadlock from './TaPadlock'
import TaFoundDoc from './TaFoundDoc'
import TaSourcesFilter from './TaSourcesFilter'
import FancySelectPicker3 from './FancySelectPicker3'
import type { Question } from '../types/question'

type SourcePickMode = 'auto' | 'manual' | 'project'
type SourceSortMode = 'relevance' | 'name' | 'date'

interface BidQuestionSourcesTabProps {
  question: Question
  isStreaming: boolean
  isLocked: boolean
  onToggleLock: () => void

  // ── Optional richer-mode props (used by ResponseBuilderTab) ─────────
  // Add Source Material button → opens find-segments modal.
  onAddSourceMaterial?: () => void
  // Find Sources empty-state CTA - when no sources are attached, render a
  // prominent button that kicks off the source-finding flow (typically the
  // same generate chain that produces sources first).
  // onFindSources?: () => void
  // Pick-mode + count controls (auto / manual / project + N for auto).
  pickMode?: SourcePickMode
  onPickModeChange?: (m: SourcePickMode) => void
  pickN?: number
  onPickNChange?: (n: number) => void
  // Sort selector for the doc list.
  sortMode?: SourceSortMode
  onSortModeChange?: (m: SourceSortMode) => void
  // Per-source delete + bulk clear. When `onRestoreSource` is also provided,
  // each delete is followed by a 6-second undo banner; the tab calls
  // `onRestoreSource(originalIndex, doc)` if the user clicks Undo.
  onDeleteSource?: (index: number) => void
  onRestoreSource?: (index: number, doc: any) => void
  onClearSources?: () => void
  // Group docs by buyer / KB and render via TaFoundDoc instead of the
  // condensed SourceDocCard. RBT uses this; BidQuestion does not.
  groupByBuyer?: boolean
  // Source filters bar above the list (include/exclude chips).
  showFilters?: boolean
  // Anything the caller wants to drop next to the title (e.g. an info chip).
  headerEndSlot?: ReactNode
}

export default function BidQuestionSourcesTab({
  question, isStreaming, isLocked, onToggleLock,
  onAddSourceMaterial, pickMode, onPickModeChange, pickN, onPickNChange,
  sortMode, onSortModeChange, onDeleteSource, onRestoreSource, onClearSources,
  groupByBuyer = false, showFilters = false, headerEndSlot,
}: BidQuestionSourcesTabProps) {

  const sources: any[] = question.sourcedocs ?? []
  const hasSources = sources.length > 0
  const buyerSources = groupByBuyer ? sources.filter((s: any) => s.buyer) : []
  const kbSources = groupByBuyer ? sources.filter((s: any) => !s.buyer) : sources

  const [showBuyer, setShowBuyer] = useState(true)
  const [showKb, setShowKb] = useState(true)
  const [includeBuyer, setIncludeBuyer] = useState(true)

  // Undo state for per-source delete. Holds the most recently removed doc
  // for 6 seconds; the banner shows during that window. Only relevant when
  // both delete + restore callbacks are wired.
  const [pendingUndo, setPendingUndo] = useState<{ index: number, doc: any } | null>(null)
  const undoTimerRef = useRef<number | null>(null)
  useEffect(() => () => {
    if (undoTimerRef.current) window.clearTimeout(undoTimerRef.current)
  }, [])

  const requestDelete = useCallback((i: number) => {
    if (!onDeleteSource) return
    const doc = sources[i]
    onDeleteSource(i)
    if (onRestoreSource && doc) {
      setPendingUndo({ index: i, doc })
      if (undoTimerRef.current) window.clearTimeout(undoTimerRef.current)
      undoTimerRef.current = window.setTimeout(() => setPendingUndo(null), 6000)
    }
  }, [onDeleteSource, onRestoreSource, sources])

  const handleUndoDelete = useCallback(() => {
    if (!pendingUndo || !onRestoreSource) return
    onRestoreSource(pendingUndo.index, pendingUndo.doc)
    setPendingUndo(null)
    if (undoTimerRef.current) window.clearTimeout(undoTimerRef.current)
  }, [pendingUndo, onRestoreSource])

  // ── Optional top-of-tab toolbar (RBT) ─────────────────────────────────────
  const showTopToolbar = !!onAddSourceMaterial || !!onPickModeChange
  const topToolbar = showTopToolbar && (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 0.5,
      mb: 1, flexWrap: 'wrap',
    }}>
      {onAddSourceMaterial && (
        <FancyButton
          disabled={isLocked}
          onClick={onAddSourceMaterial}
        >
          Add Source Material
        </FancyButton>
      )}
      <Box sx={{ flex: 1 }} />
      {onPickModeChange && pickMode && (
        <FancySelectPicker3
          labels={['Auto Pick', 'Manual', 'Project']}
          options={['auto', 'manual', 'project']}
          value={pickMode}
          onChange={(v: any) => onPickModeChange(v as SourcePickMode)}
        />
      )}
      {onPickNChange && pickMode === 'auto' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" sx={{ opacity: 0.5, fontSize: '0.7rem' }}>#</Typography>
          <input
            type="number"
            value={pickN ?? 3}
            min={0}
            max={30}
            step={1}
            onChange={e => onPickNChange(Number(e.target.value))}
            style={{
              width: 40, fontSize: 12, padding: '1px 4px',
              border: '1px solid rgba(128,128,128,0.3)', borderRadius: 4,
            }}
          />
        </Box>
      )}
    </Box>
  )

  return (
    <Box>
      {topToolbar}

      {showFilters && (
        <TaSourcesFilter
          includeList={(question as any)?.findparameters?.includelist}
          excludeList={(question as any)?.findparameters?.excludelist}
        />
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: FS_SM, px: 0.5 }}>Your Sources</Typography>
        <TaPadlock isLocked={isLocked} onToggle={onToggleLock} />
        {hasSources ? (
          <>
            <Typography variant="caption" sx={{ opacity: 0.5, fontSize: '0.68rem' }}>
              {sources.length} doc{sources.length !== 1 ? 's' : ''}
            </Typography>
            {question.totalwords != null && (
              <Typography variant="caption" sx={{ opacity: 0.5, fontSize: '0.68rem' }}>
                {question.totalwords.toLocaleString()} words
              </Typography>
            )}
            {isStreaming && (
              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'primary.main', fontStyle: 'italic' }}>
                (live)
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="caption" sx={{ opacity: 0.4, fontSize: '0.68rem' }}>
            {isStreaming ? 'Searching…' : 'None'}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {headerEndSlot}
        {onClearSources && hasSources && (
          <Tooltip title="Clear Sources">
            <span>
              <IconButton size="small" disabled={isLocked} onClick={onClearSources} sx={{ p: 0.25 }}>
                <CancelOutlinedIcon sx={{ fontSize: FS_XL }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {onSortModeChange && hasSources && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pb: 0.5 }}>
          <FancySelectPicker3
            labels={['Relevance', 'Name', 'Date']}
            options={['relevance', 'name', 'date']}
            value={sortMode ?? 'relevance'}
            onChange={(v: any) => onSortModeChange(v as SourceSortMode)}
            title="Sort"
          />
        </Box>
      )}

      {pendingUndo && (
        <Box
          data-testid="sources_undo_banner"
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            mb: 0.75, px: 1, py: 0.5,
            border: 1, borderColor: 'divider', borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Typography variant="caption" sx={{ fontSize: FS_XS, flex: 1, opacity: 0.85 }}>
            Removed "{pendingUndo.doc?.filename ?? 'source'}".
          </Typography>
          <FancyButton
            testId="sources_undo_btn"
            onClick={handleUndoDelete}
            startIcon={<UndoIcon sx={{ fontSize: 14 }} />}
          >
            Undo
          </FancyButton>
        </Box>
      )}

      {/* Empty-state CTA - when no sources are attached and nothing is
          streaming in, surface a prominent Find Sources button so the user
          can kick off the source-finding flow without hunting for it. */}
      {!hasSources && !isStreaming && (
        <Box
          data-testid="sources_findsources_cta"
          sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: 1, py: 3, px: 2,
            border: 2, borderStyle: 'dashed', borderColor: 'divider', borderRadius: 1,
            bgcolor: 'action.hover',
            my: 1,
          }}
        >
          <TravelExploreIcon sx={{ fontSize: 28, opacity: 0.55 }} />
          <Typography variant="caption" sx={{ fontSize: FS_XS, opacity: 0.7, textAlign: 'center' }}>
            No sources yet. Find sources from the knowledge base to ground this answer.
          </Typography>
          <FancyButton
            solid
            disabled={isLocked}
            onClick={() => console.log('TODO')}
            testId="sources_findsources_btn"
            startIcon={<TravelExploreIcon sx={{ fontSize: 16 }} />}
          >
            Find Sources
          </FancyButton>
        </Box>
      )}

      {!groupByBuyer && sources.map((s: any, i: number) => (
        <SourceDocCard
          key={s.sequencesha ?? s.filesha ?? i}
          doc={s}
          onDelete={onDeleteSource && !isLocked ? () => requestDelete(i) : undefined}
        />
      ))}

      {groupByBuyer && buyerSources.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', py: 0.4, gap: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: FS_XS }}>Buyer Sources</Typography>
            <Chip
              size="small"
              label={includeBuyer ? 'On' : 'Off'}
              onClick={() => setIncludeBuyer(v => !v)}
              sx={{ height: 18, fontSize: FS_MICRO, cursor: 'pointer' }}
            />
            <Box sx={{ flex: 1 }} />
            {includeBuyer && (
              <Typography variant="caption" sx={{ opacity: 0.5, fontSize: '0.68rem' }}>{buyerSources.length}</Typography>
            )}
            <Tooltip title="Collapse">
              <IconButton size="small" onClick={() => setShowBuyer(v => !v)} sx={{ p: 0.25 }}>
                {showBuyer ? <ExpandLessIcon sx={{ fontSize: FS_XL }} /> : <ExpandMoreIcon sx={{ fontSize: FS_XL }} />}
              </IconButton>
            </Tooltip>
          </Box>
          <Collapse in={showBuyer && includeBuyer}>
            {buyerSources.map((s: any, i: number) => (
              <TaFoundDoc
                key={(s as any).sha ?? i}
                source={s}
                canDelete={!isLocked && !!onDeleteSource}
                onDelete={onDeleteSource ? () => requestDelete(sources.indexOf(s)) : undefined}
                index={i}
              />
            ))}
          </Collapse>
        </Box>
      )}

      {groupByBuyer && kbSources.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', py: 0.4, gap: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: FS_XS }}>Knowledge Base Sources</Typography>
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" sx={{ opacity: 0.5, fontSize: '0.68rem' }}>{kbSources.length}</Typography>
            <Tooltip title="Collapse">
              <IconButton size="small" onClick={() => setShowKb(v => !v)} sx={{ p: 0.25 }}>
                {showKb ? <ExpandLessIcon sx={{ fontSize: FS_XL }} /> : <ExpandMoreIcon sx={{ fontSize: FS_XL }} />}
              </IconButton>
            </Tooltip>
          </Box>
          <Collapse in={showKb}>
            {kbSources.map((s: any, i: number) => (
              <TaFoundDoc
                key={(s as any).sha ?? i}
                source={s}
                canDelete={!isLocked && !!onDeleteSource}
                onDelete={onDeleteSource ? () => requestDelete(sources.indexOf(s)) : undefined}
                index={buyerSources.length + i}
              />
            ))}
          </Collapse>
        </Box>
      )}
    </Box>
  )
}
